import { useState, useEffect } from 'react'
import RegistrationModal from './RegistrationModal'
import ShareButton from './ShareButton'
import { DateTime } from 'luxon'
import './EventDetail.css'

const EventDetail = ({ event, eventType }) => {
  const [showRegistration, setShowRegistration] = useState(false)
  const [isRegistered, setIsRegistered] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [selectedVideo, setSelectedVideo] = useState(null)

  const getEventIcon = (type) => {
    switch(type?.toLowerCase()) {
      case 'school': return 'fa-graduation-cap'
      case 'seminar': return 'fa-chalkboard-teacher'
      case 'workshop': return 'fa-tools'
      default: return 'fa-calendar-alt'
    }
  }

  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'upcoming': return 'fa-calendar-plus'
      case 'ongoing': return 'fa-clock'
      case 'completed': return 'fa-check-circle'
      default: return 'fa-circle'
    }
  }

  const getInitials = (name) => {
    if (!name) return ''
    return name.split(' ').map(n => n[0]).join('').toUpperCase()
  }

  // Helper function to normalize image paths from different sources
  const normalizeImagePath = (imagePath) => {
    if (!imagePath) return ''
    
    // Full URL (http/https) - use as-is (Supabase Storage URLs)
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath
    }
    
    // Relative path starting with ../ - convert to /
    if (imagePath.startsWith('../')) {
      return imagePath.replace('../', '/')
    }
    
    // Absolute path starting with / - use as-is
    if (imagePath.startsWith('/')) {
      return imagePath
    }
    
    // Otherwise - assume it's a filename in /data/images/
    return `/data/images/${imagePath}`
  }

  // Format date from database format (YYYY-MM-DD) to readable format
  // Stored data is in Asia/Dhaka timezone
  const formatEventDate = (dateString) => {
    if (!dateString) return 'Date TBA'
    
    try {
      // Parse date assuming it's in Asia/Dhaka timezone
      const date = DateTime.fromISO(dateString, { zone: 'Asia/Dhaka' })
      if (date.isValid) {
        return date.toFormat('LLLL d, yyyy') // e.g., "January 30, 2024"
      }
      // Fallback: try parsing as-is
      return dateString
    } catch (error) {
      return dateString
    }
  }

  // Get date/time for sorting schedule items (newest first)
  const getScheduleDateTime = (session, eventDate) => {
    if (!session.time) return DateTime.fromMillis(0) // Put items without time at the end
    
    // Stored data is in Asia/Dhaka timezone
    const sourceTimezone = session.timezone || 'Asia/Dhaka'
    
    try {
      // For workshops, check if time contains full date format (e.g., "July 20, 2025, 10:00 AM")
      if (eventType === 'workshop') {
        const hasFullDate = /[A-Za-z]+\s+\d{1,2},\s+\d{4}/.test(session.time)
        if (hasFullDate) {
          const timeMatch = session.time.match(/^([A-Za-z]+\s+\d{1,2},\s+\d{4}),\s*(.+)$/)
          if (timeMatch) {
            const datePart = timeMatch[1]
            const timePart = timeMatch[2]
            const startTime = timePart.split(' - ')[0].trim()
            const dateTimeString = `${datePart}, ${startTime}`
            
            const parsedDate = DateTime.fromFormat(dateTimeString, "LLLL d, yyyy, h:mm a", {
              zone: sourceTimezone
            })
            if (parsedDate.isValid) {
              return parsedDate
            }
          }
        }
      }
      
      // Check if time already contains full date/time
      const hasFullDateTime = session.time.includes(',') && /\d{4}/.test(session.time)
      
      if (hasFullDateTime) {
        // Parse full date/time string from Asia/Dhaka timezone
        const parsedDate = DateTime.fromFormat(session.time, "EEEE, LLLL d, yyyy, h:mm a", {
          zone: sourceTimezone
        })
        if (parsedDate.isValid) {
          return parsedDate
        }
      }
      
      // If time is just a time range, combine with event date
      if (eventDate) {
        const startTime = session.time.split(' - ')[0].trim()
        const dateTimeString = `${eventDate} ${startTime}`
        
        try {
          const originalDateTime = DateTime.fromISO(dateTimeString, { zone: sourceTimezone })
          if (originalDateTime.isValid) {
            return originalDateTime
          }
        } catch {
          try {
            const originalDateTime = DateTime.fromFormat(dateTimeString, "yyyy-MM-dd h:mm a", {
              zone: sourceTimezone
            })
            if (originalDateTime.isValid) {
              return originalDateTime
            }
          } catch {}
        }
      }
    } catch (error) {
      // Ignore parsing errors
    }
    
    // Return epoch time (0) for items that can't be parsed - they'll be sorted last
    return DateTime.fromMillis(0)
  }

  // Sort schedule items by date/time (newest first)
  const sortedSchedule = event.schedule ? [...event.schedule].sort((a, b) => {
    const dateA = getScheduleDateTime(a, event.date)
    const dateB = getScheduleDateTime(b, event.date)
    // Sort descending (newest first)
    return dateB.toMillis() - dateA.toMillis()
  }) : []

  // Convert video URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return ''
    
    // YouTube URLs
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0]
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0]
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url
    }
    if (url.includes('youtube.com/embed/')) {
      return url // Already an embed URL
    }
    
    // Vimeo URLs
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0]
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url
    }
    if (url.includes('player.vimeo.com/video/')) {
      return url // Already an embed URL
    }
    
    // Direct video URLs (mp4, webm, etc.) - return as-is for <video> tag
    if (url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i)) {
      return url
    }
    
    // If it's already an iframe embed URL, return as-is
    if (url.includes('embed') || url.includes('iframe')) {
      return url
    }
    
    // Default: return original URL (might be a direct video file)
    return url
  }

  // Handle ESC key to close lightbox and video modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (selectedImage) {
          setSelectedImage(null)
        }
        if (selectedVideo) {
          setSelectedVideo(null)
        }
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [selectedImage, selectedVideo])

  const formatSessionTime = (session, eventDate = null) => {
    // If no time provided, return default
    if (!session.time) {
      return 'To be announced'
    }

    // Stored data is in Asia/Dhaka timezone
    const sourceTimezone = session.timezone || 'Asia/Dhaka'
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''

    // For workshops, handle both formats: just time ("10:00 AM - 10:15 AM") or full date/time ("July 20, 2025, 10:00 AM - 10:15 AM")
    if (eventType === 'workshop') {
      // Check if time contains a full date (month name + day + year pattern like "July 20, 2025")
      const hasFullDate = /[A-Za-z]+\s+\d{1,2},\s+\d{4}/.test(session.time)
      
      if (hasFullDate) {
        // Full date format: handle timezone conversion similar to seminars
        try {
          // Format: "July 20, 2025, 10:00 AM - 10:15 AM" or "July 20, 2025, 10:00 AM"
          // Try to parse with Luxon - handle both with and without time range
          const timeMatch = session.time.match(/^([A-Za-z]+\s+\d{1,2},\s+\d{4}),\s*(.+)$/)
          if (timeMatch) {
            const datePart = timeMatch[1] // "July 20, 2025"
            const timePart = timeMatch[2] // "10:00 AM - 10:15 AM" or "10:00 AM"
            
            const startTime = timePart.split(' - ')[0].trim()
            const dateTimeString = `${datePart}, ${startTime}`
            
            // Try parsing with format "LLLL d, yyyy, h:mm a" (no day name, like seminars but different format)
            // Parse from Asia/Dhaka timezone
            const parsedDate = DateTime.fromFormat(dateTimeString, "LLLL d, yyyy, h:mm a", {
              zone: sourceTimezone
            })
            
            if (parsedDate.isValid) {
              const userDateTime = parsedDate.setZone(userTimezone)
              const formattedStart = userDateTime.toFormat("LLLL d, yyyy, h:mm a")
              
              // If there's an end time, convert it too
              if (timePart.includes(' - ')) {
                const endTime = timePart.split(' - ')[1].trim()
                const endDateTimeString = `${datePart}, ${endTime}`
                const originalEnd = DateTime.fromFormat(endDateTimeString, "LLLL d, yyyy, h:mm a", {
                  zone: sourceTimezone
                })
                
                if (originalEnd.isValid) {
                  const userEndDateTime = originalEnd.setZone(userTimezone)
                  const formattedEnd = userEndDateTime.toFormat("h:mm a")
                  return `${formattedStart} - ${formattedEnd} (Timezone: ${userTimezone})`
                }
              }
              
              return `${formattedStart} (Timezone: ${userTimezone})`
            }
          }
        } catch (error) {
          // If parsing fails, return original time
        }
        // Parsing failed: return as-is
        return session.time
      }
      // For just time format, fall through to the general logic below (same as seminars)
    }

    // Check if time already contains full date/time format (e.g., "Sunday, February 9, 2025, 10:00 PM")
    const hasFullDateTime = session.time.includes(',') && /\d{4}/.test(session.time)
    
    if (hasFullDateTime) {
      // Time already contains full date/time, convert from Asia/Dhaka to user timezone
      try {
        // Try to parse the full date/time string
        const parsedDate = DateTime.fromFormat(session.time, "EEEE, LLLL d, yyyy, h:mm a", {
          zone: sourceTimezone
        })
        if (parsedDate.isValid) {
          const userDateTime = parsedDate.setZone(userTimezone)
          return userDateTime.toFormat("EEEE, LLLL d, yyyy, h:mm a") + ` (Timezone: ${userTimezone})`
        }
      } catch (error) {
        // If parsing fails, return original time
      }
      return session.time
    }

    // If time is just a time range (e.g., "10:00 AM - 10:45 AM"), combine with event date
    if (eventDate) {
      try {
        // Extract start time from range
        const startTime = session.time.split(' - ')[0].trim()
        // Combine event date with start time
        const dateTimeString = `${eventDate} ${startTime}`
        
        // Try parsing with different date formats, from Asia/Dhaka timezone
        let originalDateTime
        try {
          // Try ISO format first
          originalDateTime = DateTime.fromISO(dateTimeString, { zone: sourceTimezone })
        } catch {
          // Fallback to format string
          originalDateTime = DateTime.fromFormat(dateTimeString, "yyyy-MM-dd h:mm a", {
            zone: sourceTimezone
          })
        }
        
        if (originalDateTime.isValid) {
          const userDateTime = originalDateTime.setZone(userTimezone)
          const formattedStart = userDateTime.toFormat("EEEE, LLLL d, yyyy, h:mm a")
          
          // If there's an end time, convert it too
          if (session.time.includes(' - ')) {
            const endTime = session.time.split(' - ')[1].trim()
            try {
              const endDateTimeString = `${eventDate} ${endTime}`
              let originalEndDateTime
              try {
                originalEndDateTime = DateTime.fromISO(endDateTimeString, { zone: sourceTimezone })
              } catch {
                originalEndDateTime = DateTime.fromFormat(endDateTimeString, "yyyy-MM-dd h:mm a", {
                  zone: sourceTimezone
                })
              }
              
              if (originalEndDateTime.isValid) {
                const userEndDateTime = originalEndDateTime.setZone(userTimezone)
                const formattedEnd = userEndDateTime.toFormat("h:mm a")
                return `${formattedStart} - ${formattedEnd} (Timezone: ${userTimezone})`
              }
            } catch (e) {
              // If end time conversion fails, just return start time
            }
          }
          
          return `${formattedStart} (Timezone: ${userTimezone})`
        }
      } catch (error) {
        // If parsing fails, return original time
      }
    }

    // Fallback: return the time as-is
    return session.time
  }

  return (
    <div className="event-detail-wrapper">
      {/* Hero Section */}
      <div className="event-hero">
        <div className="event-hero-background"></div>
        <div className="container">
          <div className="event-hero-content">
            <div className="event-hero-badges">
              <span className={`event-type-badge ${eventType}-badge`}>
                <i className={`fas ${getEventIcon(event.type)}`}></i>
                {event.type}
              </span>
              <span className={`event-status-badge ${event.status?.toLowerCase()}`}>
                <i className={`fas ${getStatusIcon(event.status)}`}></i>
                {event.status}
              </span>
            </div>
            <h1 className="event-hero-title">{event.title}</h1>
            <p className="event-hero-subtitle">{event.subtitle}</p>
            
            <div className="event-hero-meta">
              <div className="hero-meta-item">
                <div className="hero-meta-icon">
                  <i className="fas fa-calendar"></i>
                </div>
                <div className="hero-meta-content">
                  <span className="hero-meta-label">Date</span>
                  <span className="hero-meta-value">{formatEventDate(event.date)}</span>
                </div>
              </div>
              {event.time && (
                <div className="hero-meta-item">
                  <div className="hero-meta-icon">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="hero-meta-content">
                    <span className="hero-meta-label">Time</span>
                    <span className="hero-meta-value">{event.time}</span>
                  </div>
                </div>
              )}
              <div className="hero-meta-item">
                <div className="hero-meta-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div className="hero-meta-content">
                  <span className="hero-meta-label">Location</span>
                  <span className="hero-meta-value">{event.location}</span>
                </div>
              </div>
              <div className="hero-meta-item">
                <div className="hero-meta-icon">
                  <i className="fas fa-users"></i>
                </div>
                <div className="hero-meta-content">
                  <span className="hero-meta-label">Participants</span>
                  <span className="hero-meta-value">{event.currentEnrollment}/{event.capacity}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="event-detail-layout">
          {/* Main Content */}
          <div className="event-main-content">
            {/* Overview Section */}
            <section className="event-section-modern">
              <div className="section-header">
                <div className="section-icon">
                  <i className="fas fa-info-circle"></i>
                </div>
                <h2 className="section-title">Overview</h2>
              </div>
              <div className="section-content">
                <p className="overview-text">{event.overview}</p>
              </div>
            </section>

            {/* Learning Objectives */}
            {event.learning_objectives && event.learning_objectives.length > 0 && (
              <section className="event-section-modern">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-bullseye"></i>
                  </div>
                  <h2 className="section-title">Learning Objectives</h2>
                </div>
                <div className="section-content">
                  <div className="objectives-grid">
                    {event.learning_objectives.map((objective, idx) => (
                      <div key={idx} className="objective-card">
                        <div className="objective-number">{idx + 1}</div>
                        <p className="objective-text">{objective}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Target Audience */}
            {event.target_audience && event.target_audience.length > 0 && (
              <section className="event-section-modern">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-user-graduate"></i>
                  </div>
                  <h2 className="section-title">Target Audience</h2>
                </div>
                <div className="section-content">
                  <div className="audience-grid">
                    {event.target_audience.map((item, idx) => (
                      <div key={idx} className="audience-chip">
                        <i className="fas fa-user"></i>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Prerequisites */}
            {event.prerequisites && event.prerequisites.length > 0 && (
              <section className="event-section-modern">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-list-check"></i>
                  </div>
                  <h2 className="section-title">Prerequisites</h2>
                </div>
                <div className="section-content">
                  <ul className="prerequisites-list-modern">
                    {event.prerequisites.map((prerequisite, idx) => (
                      <li key={idx} className="prerequisite-item">
                        <i className="fas fa-check-circle"></i>
                        <span>{prerequisite}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}

            {/* Required Materials */}
            {event.materials && event.materials.length > 0 && (
              <section className="event-section-modern">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-toolbox"></i>
                  </div>
                  <h2 className="section-title">Required Materials</h2>
                </div>
                <div className="section-content">
                  <ul className="prerequisites-list-modern">
                    {event.materials.map((material, idx) => (
                      <li key={idx} className="prerequisite-item">
                        <i className="fas fa-toolbox"></i>
                        <span>{material}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}

            {/* Schedule/Speeches */}
            {event.schedule && event.schedule.length > 0 && (
              <section className="event-section-modern">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <h2 className="section-title">{eventType === 'seminar' ? 'Talks' : 'Schedule'}</h2>
                </div>
                <div className="section-content">
                  <div className="schedule-modern">
                    {sortedSchedule.map((session, idx) => {
                      const formattedTime = formatSessionTime(session, event.date)
                      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
                      let isUpcoming = false
                      
                      // Check if session is upcoming
                      if (session.time) {
                        try {
                          // Stored data is in Asia/Dhaka timezone
                          const sourceTimezone = session.timezone || 'Asia/Dhaka'
                          let originalDateTime
                          
                          // For workshops, check if time contains full date format
                          if (eventType === 'workshop') {
                            const hasFullDate = /[A-Za-z]+\s+\d{1,2},\s+\d{4}/.test(session.time)
                            if (hasFullDate) {
                              const timeMatch = session.time.match(/^([A-Za-z]+\s+\d{1,2},\s+\d{4}),\s*(.+)$/)
                              if (timeMatch) {
                                const datePart = timeMatch[1]
                                const timePart = timeMatch[2]
                                const startTime = timePart.split(' - ')[0].trim()
                                const dateTimeString = `${datePart}, ${startTime}`
                                
                                originalDateTime = DateTime.fromFormat(dateTimeString, "LLLL d, yyyy, h:mm a", {
                                  zone: sourceTimezone
                                })
                              }
                            }
                          }
                          
                          // Check if time already contains full date/time
                          if (!originalDateTime) {
                            const hasFullDateTime = session.time.includes(',') && /\d{4}/.test(session.time)
                            
                            if (hasFullDateTime) {
                              // Parse full date/time string from Asia/Dhaka timezone
                              originalDateTime = DateTime.fromFormat(session.time, "EEEE, LLLL d, yyyy, h:mm a", {
                                zone: sourceTimezone
                              })
                            } else if (event.date) {
                              // Combine event date with session time
                              const startTime = session.time.split(' - ')[0].trim()
                              const dateTimeString = `${event.date} ${startTime}`
                              try {
                                originalDateTime = DateTime.fromISO(dateTimeString, { zone: sourceTimezone })
                              } catch {
                                originalDateTime = DateTime.fromFormat(dateTimeString, "yyyy-MM-dd h:mm a", {
                                  zone: sourceTimezone
                                })
                              }
                            }
                          }
                          
                          if (originalDateTime && originalDateTime.isValid) {
                            const userDateTime = originalDateTime.setZone(userTimezone)
                            const userDateTimeNow = DateTime.now().setZone(userTimezone)
                            isUpcoming = userDateTime > userDateTimeNow
                          }
                        } catch (e) {
                          // Ignore parsing errors
                        }
                      }

                      return (
                        <div key={idx} className={`schedule-card ${isUpcoming ? 'upcoming' : ''}`}>
                          <div className="schedule-card-header">
                            <div className="schedule-date-badge">
                              <i className="far fa-calendar"></i>
                              <span>{formattedTime}</span>
                            </div>
                            {isUpcoming && (
                              <span className="upcoming-indicator">Upcoming</span>
                            )}
                          </div>
                          <div className="schedule-card-body">
                            <h3 className="schedule-title">{session.title}</h3>
                            {session.description && (
                              <p className="schedule-description">{session.description}</p>
                            )}
                            
                            {session.speaker && session.speaker.name && (
                              <div className="schedule-speaker-card">
                                <div className="schedule-speaker-avatar">
                                  {getInitials(session.speaker.name)}
                                </div>
                                <div className="schedule-speaker-info">
                                  <div className="schedule-speaker-name">{session.speaker.name}</div>
                                  {(session.speaker.title || session.speaker.organization) && (
                                    <div className="schedule-speaker-title">
                                      {[session.speaker.title, session.speaker.organization].filter(Boolean).join(' · ')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {session.tags && session.tags.length > 0 && (
                              <div className="schedule-tags">
                                {session.tags.map((tag, tagIdx) => (
                                  <span key={tagIdx} className="schedule-tag">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}

                            {session.video_link && (
                              <button
                                onClick={() => setSelectedVideo({
                                  url: session.video_link,
                                  title: session.title
                                })}
                                className="schedule-video-btn"
                              >
                                <i className="fas fa-play"></i>
                                <span>Watch Recording</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </section>
            )}

            {/* Speakers */}
            {event.speakers && event.speakers.length > 0 && (
              <section className="event-section-modern">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <h2 className="section-title">{eventType === 'seminar' ? 'Speakers' : 'Instructors'}</h2>
                </div>
                <div className="section-content">
                  <div className="speakers-grid-modern">
                    {event.speakers.map((speaker, idx) => (
                      <div key={idx} className="speaker-card-modern">
                        <div className="speaker-card-avatar">
                          {getInitials(speaker.name)}
                        </div>
                        <div className="speaker-card-content">
                          <h3 className="speaker-card-name">{speaker.name}</h3>
                          <p className="speaker-card-title">{speaker.title}</p>
                          <p className="speaker-card-org">{speaker.organization}</p>
                          {speaker.bio && (
                            <p className="speaker-card-bio">{speaker.bio}</p>
                          )}
                          {speaker.topics && speaker.topics.length > 0 && (
                            <div className="speaker-card-topics">
                              {speaker.topics.map((topic, topicIdx) => (
                                <span key={topicIdx} className="speaker-topic-tag">
                                  {topic}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Gallery */}
            {event.gallery && event.gallery.length > 0 && (
              <section className="event-section-modern">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-images"></i>
                  </div>
                  <h2 className="section-title">Gallery</h2>
                </div>
                <div className="section-content">
                  <div className="gallery-modern">
                    {event.gallery.map((item, idx) => {
                      const imagePath = normalizeImagePath(item.image)
                      
                      return (
                        <div 
                          key={idx} 
                          className="gallery-item-modern"
                          onClick={() => setSelectedImage({ image: imagePath, caption: item.caption })}
                        >
                          <div className="gallery-image-wrapper">
                            <img src={imagePath} alt={item.caption || 'Gallery Image'} />
                            <div className="gallery-overlay">
                              <i className="fas fa-search-plus"></i>
                            </div>
                          </div>
                          {item.caption && (
                            <p className="gallery-caption-modern">{item.caption}</p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="event-sidebar-modern">
            <div className="sidebar-card">
              <h3 className="sidebar-title">Event Details</h3>
              <div className="sidebar-details">
                <div className="sidebar-detail-item">
                  <i className="fas fa-language"></i>
                  <div>
                    <span className="sidebar-detail-label">Language</span>
                    <span className="sidebar-detail-value">{event.language}</span>
                  </div>
                </div>
                <div className="sidebar-detail-item">
                  <i className="fas fa-dollar-sign"></i>
                  <div>
                    <span className="sidebar-detail-label">Price</span>
                    <span className="sidebar-detail-value">${event.price}</span>
                  </div>
                </div>
              </div>
            </div>

            {event.status !== 'Completed' && (
              <div className="sidebar-card">
                <div className="sidebar-actions">
                  <button
                    className={`sidebar-register-btn ${isRegistered ? 'registered' : ''}`}
                    onClick={() => setShowRegistration(true)}
                    disabled={isRegistered}
                  >
                    <i className={`fas ${isRegistered ? 'fa-check-circle' : 'fa-ticket-alt'}`}></i>
                    <span>{isRegistered ? 'Registered' : 'Register Now'}</span>
                  </button>
                  <ShareButton event={event} />
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      {showRegistration && (
        <RegistrationModal
          event={event}
          eventType={eventType}
          onClose={() => setShowRegistration(false)}
          onSuccess={() => {
            setIsRegistered(true)
            setShowRegistration(false)
          }}
        />
      )}

      {/* Image Lightbox */}
      {selectedImage && (
        <div 
          className="image-lightbox"
          onClick={() => setSelectedImage(null)}
        >
          <div className="lightbox-overlay"></div>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="lightbox-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close"
            >
              <i className="fas fa-times"></i>
            </button>
            <div className="lightbox-image-wrapper">
              <img src={selectedImage.image} alt={selectedImage.caption || 'Gallery Image'} />
            </div>
            {selectedImage.caption && (
              <div className="lightbox-caption">
                <p>{selectedImage.caption}</p>
              </div>
            )}
            {/* Navigation buttons */}
            {event.gallery && event.gallery.length > 1 && (
              <>
                <button 
                  className="lightbox-nav lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation()
                    const currentIndex = event.gallery.findIndex(
                      item => normalizeImagePath(item.image) === selectedImage.image
                    )
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : event.gallery.length - 1
                    const prevItem = event.gallery[prevIndex]
                    const prevPath = normalizeImagePath(prevItem.image)
                    setSelectedImage({ image: prevPath, caption: prevItem.caption })
                  }}
                  aria-label="Previous image"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button 
                  className="lightbox-nav lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation()
                    const currentIndex = event.gallery.findIndex(
                      item => normalizeImagePath(item.image) === selectedImage.image
                    )
                    const nextIndex = currentIndex < event.gallery.length - 1 ? currentIndex + 1 : 0
                    const nextItem = event.gallery[nextIndex]
                    const nextPath = normalizeImagePath(nextItem.image)
                    setSelectedImage({ image: nextPath, caption: nextItem.caption })
                  }}
                  aria-label="Next image"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="video-modal"
          onClick={() => setSelectedVideo(null)}
        >
          <div className="video-modal-overlay"></div>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3 className="video-modal-header-title">
                <i className="fas fa-play-circle"></i>
                <span>Video Recording</span>
              </h3>
              <button 
                className="video-modal-close"
                onClick={() => setSelectedVideo(null)}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="video-modal-wrapper">
              {(() => {
                const embedUrl = getEmbedUrl(selectedVideo.url)
                const isDirectVideo = embedUrl.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i)
                
                if (isDirectVideo) {
                  // Direct video file - use <video> tag
                  return (
                    <video
                      controls
                      autoPlay
                      className="video-player"
                      src={embedUrl}
                    >
                      Your browser does not support the video tag.
                    </video>
                  )
                } else {
                  // YouTube, Vimeo, or other embed URLs - use iframe
                  return (
                    <iframe
                      className="video-player"
                      src={embedUrl}
                      title={selectedVideo.title || 'Video Recording'}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  )
                }
              })()}
            </div>
            {selectedVideo.title && (
              <div className="video-modal-footer">
                <h4 className="video-modal-title">{selectedVideo.title}</h4>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default EventDetail
